import { useContext, type ReactNode } from "react";
import { Popover } from "radix-ui";
import { BookOpen } from "lucide-react";
import Text from "../../../../primitives/components/Text/Text";
import { GlossaryDescriptionsCtx } from "./GlossaryDescriptionsCtx";
import { styles } from "./glossaryEntry.css";

type GlossaryEntryProps = {
  term: string;
  children: ReactNode;
};

const FAILED_TEXT = "Beschreibung konnte nicht geladen werden";

/**
 * Renders a glossary term detected in step content as an inline trigger that
 * opens its description in a popover.
 *
 * Descriptions load lazily (see `GlossaryDescriptionsCtx`): the map is `null`
 * while in flight, so a missing entry only counts as "failed to load" once the
 * map has landed. A successfully loaded term shows accented and reveals its
 * description; a failed one inherits the surrounding colour and explains that
 * the description could not be loaded.
 *
 * The popover is portalled and Radix collision-aware, keeping it within the
 * viewport regardless of where the term sits in the content.
 */
const GlossaryEntry = ({ term, children }: GlossaryEntryProps) => {
  const descriptions = useContext(GlossaryDescriptionsCtx);
  const description = descriptions?.get(term)?.description;
  const failed = descriptions !== null && !description;

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={failed ? styles.termFailed : styles.term}
          data-glossary-term={term}
        >
          <BookOpen className={styles.icon} aria-hidden />
          {children}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={`${styles.content} ${styles.theme}`}
          sideOffset={6}
          collisionPadding={8}
        >
          <Text variant="label-large" color="neutral-0">
            {term}
          </Text>
          <Text variant="body" color="neutral-100">
            {description || FAILED_TEXT}
          </Text>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default GlossaryEntry;
